import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import {
  NavController,
  AlertController,
  ModalController, IonicModule, ActionSheetController, IonSelect, IonSlides
} from '@ionic/angular';
import { Location } from '@angular/common';
import { FormServiceService } from 'src/app/services/publications/form-service.service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import Swal2 from 'sweetalert2';
import { RequestService } from 'src/app/services/request/request.service';
import { Subscription } from 'rxjs';
import { PagoVerificacionComponent } from 'src/app/components/pago-verificacion/pago-verificacion.component';
import { SocialInteractionNotificationService } from 'src/app/services/notifications/social-interaction-notification.service';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { NativeLiquidGlass } from 'src/app/plugins/native-runtime.plugins';


@Component({
  selector: 'app-stock-keeping-unit-selection',
  templateUrl: './stock-keeping-unit-selection.component.html',
  styleUrls: ['./stock-keeping-unit-selection.component.scss'],
})
export class StockKeepingUnitSelectionComponent implements OnInit, OnDestroy {
  complete_data;
  @Input() image;
  @Input() data;
  @Input() product_id;
  @Input() product_name;
  @Input() product_details;
  displayImage: string | null = null;
  cart: any[] = [];
  checkoutActionLoading: 'cart' | 'buy' | null = null;
  reservationCountdown: string | null = null;
  reservationProgress = 0;
  reservationEndsAtLabel: string | null = null;
  inventorySyncedAtLabel: string | null = null;
  skuLoadError = false;
  private lastLoadContext: {
    productId: string;
    productName: string | null;
    productDetails: boolean;
    image: string | null;
  } | null = null;
  private reservationInterval: ReturnType<typeof setInterval> | null = null;
  private routeSubscription?: Subscription;
  private nativeFooterListener?: PluginListenerHandle;
  private nativeFooterSyncTimers: Array<ReturnType<typeof setTimeout>> = [];
  private selectionLoadToken = 0;
  private readonly fallbackImage = 'https://laniakea-multimedia.s3.amazonaws.com/laniakea.tv/Sin+resultados-2.gif';

  /** Returns true if the given URL is the known bad Laniakea multimedia fallback GIF */
  isFallbackImage(url: string | null | undefined): boolean {
    if (!url) return true;
    return url.includes('Sin+resultados-2.gif') || url === this.fallbackImage;
  }
  constructor(
    public modalController: ModalController,
    public formservice:FormServiceService,
    private router:Router,
    private request: RequestService,
    private route: ActivatedRoute,
    private location: Location,
    private socialNotifications: SocialInteractionNotificationService,
  ) { }

  ngOnInit() {
    this.hideFeexNativeChrome();
    this.syncCompactSkuFooter();
    if (this.isRoutedSkuPage() && Capacitor.getPlatform() === 'ios') {
      NativeLiquidGlass.addListener('footerAction', ({ action }) => {
        // Native collapsed footers intentionally emit this generic action.
        // On the SKU route it means "leave the focused SKU and return to
        // FEEX", which is exactly what the compact ecosystem control should
        // do here.
        if (action === 'footer-return-to-products') {
          void this.dismiss();
        }
      }).then(listener => this.nativeFooterListener = listener).catch(() => undefined);
    }
    this.updateReservationPresentation();
    this.reservationInterval = setInterval(() => this.updateReservationPresentation(), 1000);
    this.routeSubscription = this.route.queryParamMap.subscribe((params) => {
      void this.loadSelectionFromParams(params);
    });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('native-sku-active');
    this.nativeFooterListener?.remove();
    this.nativeFooterListener = undefined;
    this.nativeFooterSyncTimers.forEach(timer => clearTimeout(timer));
    this.nativeFooterSyncTimers = [];
    if (this.reservationInterval) {
      clearInterval(this.reservationInterval);
      this.reservationInterval = null;
    }
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
      this.routeSubscription = undefined;
    }
  }

  private hideFeexNativeChrome(): void {
    document.body.classList.add('native-sku-active');
    document.body.classList.remove('native-dropship-header', 'native-feex-slide-nav');
    if (Capacitor.getPlatform() !== 'ios') return;
    NativeLiquidGlass.setRegion({ region: 'dropship-header', visible: false }).catch(() => undefined);
    NativeLiquidGlass.setRegion({ region: 'feex-slide-nav', visible: false }).catch(() => undefined);
  }

  private isRoutedSkuPage(): boolean {
    return String(this.router.url || '').split('?')[0].split('#')[0].toLowerCase() === '/stockkeepingunit';
  }

  /**
   * Do not let a full dropship dock survive behind the SKU purchase controls.
   * We ask UIKit more than once because Ionic's NavigationEnd and the native
   * footer animation are not guaranteed to complete in the same frame.
   */
  private syncCompactSkuFooter(): void {
    if (!this.isRoutedSkuPage() || Capacitor.getPlatform() !== 'ios') return;

    this.formservice.mediaTemplate = true;
    this.formservice.feexNativeFooterExpanded = false;
    document.body.classList.remove('native-dropship-footer');
    this.nativeFooterSyncTimers.forEach(timer => clearTimeout(timer));
    this.nativeFooterSyncTimers = [0, 160, 520].map(delay => setTimeout(() => {
      if (!this.isRoutedSkuPage()) return;
      NativeLiquidGlass.setRegion({
        region: 'footer',
        visible: true,
        mode: 'standard',
        selectedAction: 'ecosystem-page',
        collapsed: true,
      }).catch(() => undefined);
    }, delay));
  }

  private getBooleanQueryParam(value: string | null, fallback = false): boolean {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    return value === 'true' || value === '1';
  }

  private normalizeImageValue(value: any): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
      return null;
    }

    return trimmed;
  }

  private async loadSelectionFromParams(params: ParamMap): Promise<void> {
    const routeProductId = this.normalizeImageValue(params.get('product_id'));
    const routeProductName = this.normalizeImageValue(params.get('product_name'));
    const routeImage = this.normalizeImageValue(params.get('image'));
    const hasRouteContext = params.keys.length > 0;
    const productDetails = this.getBooleanQueryParam(
      params.get('product_details'),
      !!this.formservice.stockkeepingunit?.product_details,
    );

    if (hasRouteContext && routeProductId) {
      await this.loadFreshSelection(routeProductId, routeProductName, productDetails, routeImage);
      return;
    }

    await this.loadFromFallbackState(true);
  }

  private async loadFreshSelection(
    productId: string,
    productName: string | null,
    productDetails: boolean,
    routeImage: string | null,
  ): Promise<void> {
    const loadToken = ++this.selectionLoadToken;
    this.skuLoadError = false;
    this.lastLoadContext = { productId, productName, productDetails, image: routeImage };
    // Avoid setting the known bad S3 fallback. The template conditionally hides the <img> tag.
    this.displayImage = routeImage || null;

    try {
      let data = await this.request.createRequestPost('AlibabaProductHandler', { product_id: productId });

      if (data && typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (error) {
          console.error('Error parsing SKU data response:', error);
        }
      }

      if (loadToken !== this.selectionLoadToken) {
        return;
      }

      this.applySelectionState(data, productId, productName, productDetails, routeImage);
    } catch (error) {
      console.error('Error loading SKU selection:', error);
      if (loadToken !== this.selectionLoadToken) {
        return;
      }

      await this.loadFromFallbackState(false);
      this.skuLoadError = !this.data;
    }
  }

  retrySkuLoad(): void {
    if (!this.lastLoadContext) {
      return;
    }

    void this.loadFreshSelection(
      this.lastLoadContext.productId,
      this.lastLoadContext.productName,
      this.lastLoadContext.productDetails,
      this.lastLoadContext.image,
    );
  }

  async openPremiumVerification(): Promise<void> {
    const profile = this.formservice?.profile;

    if (!profile) {
      Swal2.fire({
        title: 'Inicia sesión',
        text: 'Necesitas iniciar sesión para activar Premium.',
        icon: 'info',
        timer: 4200,
        timerProgressBar: true,
        toast: true,
        position: 'top-start',
        showConfirmButton: false,
        background: '#010f4e',
        color: '#ffffff',
      });
      this.router.navigate(['/login']);
      return;
    }

    if (profile?.kyc === 'Validado') {
      Swal2.fire({
        title: 'Premium activo',
        text: 'Tu perfil ya está verificado.',
        icon: 'success',
        timer: 3600,
        timerProgressBar: true,
        toast: true,
        position: 'top-start',
        showConfirmButton: false,
        background: '#010f4e',
        color: '#ffffff',
      });
      return;
    }

    const modal = await this.modalController.create({
      component: PagoVerificacionComponent,
      cssClass: 'detailsInModal',
      canDismiss: true,
      componentProps: {
        pr: profile,
      },
    });
    await modal.present();
  }

  private async loadFromFallbackState(preferInputs: boolean): Promise<void> {
    const fallback = this.formservice.stockkeepingunit;
    const inputData = this.data;
    const hasInputSelection = !!inputData && !!this.product_id;

    const selectedData = preferInputs && hasInputSelection
      ? inputData
      : fallback?.data ?? inputData;

    const selectedProductId = preferInputs && hasInputSelection
      ? this.product_id
      : fallback?.product_id ?? this.product_id;

    const selectedProductName = preferInputs && hasInputSelection
      ? this.product_name
      : fallback?.product_name ?? this.product_name;

    const selectedProductDetails = preferInputs && hasInputSelection
      ? !!this.product_details
      : !!(fallback?.product_details ?? this.product_details);

    const selectedImage = preferInputs && hasInputSelection
      ? this.normalizeImageValue(this.image)
      : this.normalizeImageValue(fallback?.image ?? this.image);

    if (!selectedData) {
      return;
    }

    this.applySelectionState(
      selectedData,
      selectedProductId,
      selectedProductName,
      selectedProductDetails,
      selectedImage,
    );
  }

  private applySelectionState(
    data: any,
    productId: string | null,
    productName: string | null,
    productDetails: boolean,
    image: string | null,
  ): void {
    this.data = data;
    this.product_id = productId;
    this.product_name = productName;
    this.product_details = productDetails;
    this.image = image;
    // Do not fall back to the bad S3 GIF here. The template will simply not render the <img> tag
    // if the value is the known fallback (or falsy).
    this.displayImage = image || null;

    if (this.formservice.stockkeepingunit) {
      this.formservice.stockkeepingunit.data = data;
      this.formservice.stockkeepingunit.product_id = productId;
      this.formservice.stockkeepingunit.product_name = productName;
      this.formservice.stockkeepingunit.product_details = productDetails;
      this.formservice.stockkeepingunit.image = image;
    }

    this.complete_data = this.data;
    this.inventorySyncedAtLabel = new Date().toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
    });
    // Only attach a real image (we no longer store the bad S3 fallback in displayImage)
    if (this.product_details && this.displayImage) {
      this.data.image = this.displayImage;
    }

    const skuContainer = this.data?.success?.aliexpress_ds_product_get_response?.result?.ae_item_sku_info_dtos;
    if (!skuContainer || !skuContainer.ae_item_sku_info_d_t_o) {
      console.error("Invalid data structure: 'ae_item_sku_info_d_t_o' not found in this.data.");
      return;
    }

    this.data = skuContainer;

    this.data.ae_item_sku_info_d_t_o.forEach((group) => {
      if (group.selectedQuantity === undefined) {
        group.selectedQuantity = 0;
      }
    });

    for (const item of this.data.ae_item_sku_info_d_t_o) {
      if (item.sku_attr && item.sku_price) {
        const price = parseFloat(item.sku_price.replace(/[^0-9.-]+/g, ''));
        if (!isNaN(price)) {
          item.sku_price = (price * this.formservice.ds_margin).toFixed(2);
        }
      }
    }
  }

  private updateReservationPresentation(): void {
    const expiresAt = this.formservice.dsReservationExpiresAt;
    if (!expiresAt) {
      this.reservationCountdown = null;
      this.reservationProgress = 0;
      this.reservationEndsAtLabel = null;
      return;
    }

    const remainingMs = expiresAt - Date.now();
    if (remainingMs <= 0) {
      this.formservice.dsReservationExpiresAt = null;
      this.reservationCountdown = null;
      this.reservationProgress = 0;
      this.reservationEndsAtLabel = null;
      return;
    }

    const totalSeconds = Math.floor(remainingMs / 1000);
    const hours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    this.reservationCountdown = `${hours}:${minutes}:${seconds}`;
    this.reservationProgress = Math.max(0, Math.min(100, (remainingMs / this.formservice.dsReservationDurationMs) * 100));
    this.reservationEndsAtLabel = new Date(expiresAt).toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      month: 'short',
      day: 'numeric',
    });
  }
  updateCart(skuGroup: any, sku_image?): void {
    try {
    // Ensure the `cart` array exists and is initialized
    if (!this.cart) {
      this.cart = [];
    }

    // Ensure the `sku_id` exists and `selectedQuantity` is valid
    if (!skuGroup.sku_id || skuGroup.selectedQuantity == null) {
      return;
    }

    // Find the existing item in the cart
    const existingItem = this.cart.find((item) => item.sku_id === skuGroup.sku_id);

    if (skuGroup.selectedQuantity > 0) {
      if (existingItem) {
        // Update the quantity if the item is already in the cart
        existingItem.selectedQuantity = skuGroup.selectedQuantity;
      } else {
        // Add the item to the cart if it doesn't exist
        this.cart.push({
          sku_id: skuGroup.sku_id,
          price: skuGroup.sku_price,
          selectedQuantity: skuGroup.selectedQuantity,
          product_id:this.product_id,
          item_image:sku_image ?? null,
          product_name:this.product_name,
          sku_attr:skuGroup.sku_attr

        });
      }
    } else {
      // Remove the item from the cart if quantity is 0
      this.cart = this.cart.filter((item) => item.sku_id !== skuGroup.sku_id);
    }

    console.log(this.cart); // Debugging: Log the updated cart
  } catch (error) {
    console.error('Error updating quantity:', error);
    // Optional: show a user-friendly message, log to monitoring service, etc.
  }
  }

  getSelectedUnits(): number {
    return this.cart?.reduce((total, item) => total + Number(item.selectedQuantity || 0), 0) || 0;
  }

  getCartSubtotal(): string {
    return this.formatMoney(this.calculateTotal() || 0);
  }

  formatMoney(value: any): string {
    const amount = Number(value || 0);
    return amount.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  isSkuSelected(skuGroup: any): boolean {
    return Number(skuGroup?.selectedQuantity || 0) > 0;
  }

  getAvailableStock(skuGroup: any): number {
    return Number(skuGroup?.sku_available_stock || 0);
  }

  hasKnownStock(skuGroup: any): boolean {
    return skuGroup?.sku_available_stock !== null && skuGroup?.sku_available_stock !== undefined;
  }

  getSkuLabel(skuGroup: any): string {
    return (skuGroup?.sku_attr || skuGroup?.sku_id || 'Variante disponible').toString();
  }

  getSkuProperties(skuGroup: any): any[] {
    const properties = skuGroup?.ae_sku_property_dtos?.ae_sku_property_d_t_o;
    return Array.isArray(properties) ? properties : [];
  }

  getSkuImage(skuGroup: any): string | null {
    const imageProperty = this.getSkuProperties(skuGroup).find((property) => !!property?.sku_image);
    return imageProperty?.sku_image ?? null;
  }

  getRemainingStock(skuGroup: any): number {
    if (!this.hasKnownStock(skuGroup)) {
      return 0;
    }

    return Math.max(0, this.getAvailableStock(skuGroup) - Number(skuGroup?.selectedQuantity || 0));
  }

  isSkuOutOfStock(skuGroup: any): boolean {
    if (!this.hasKnownStock(skuGroup)) {
      return false;
    }

    return this.getAvailableStock(skuGroup) <= 0;
  }

  isSkuAtMax(skuGroup: any): boolean {
    if (!this.hasKnownStock(skuGroup)) {
      return false;
    }

    return !this.isSkuOutOfStock(skuGroup) && Number(skuGroup?.selectedQuantity || 0) >= this.getAvailableStock(skuGroup);
  }

  // Method to handle increment and decrement buttons
  updateQuantity(skuGroup: any, change: number, sku_image?): void {
    console.log('sku_image',sku_image)
    // Ensure selectedQuantity is initialized
    if (skuGroup.selectedQuantity == null) {
      skuGroup.selectedQuantity = 0;
    }

    const newQuantity = skuGroup.selectedQuantity + change;

    // Validate the new quantity against stock limits
    if (newQuantity >= 0 && this.hasKnownStock(skuGroup) && newQuantity <= this.getAvailableStock(skuGroup)) {
      console.log('newQuantity',newQuantity)
      skuGroup.selectedQuantity = newQuantity;
      this.updateCart(skuGroup, sku_image);
    }
  }
  
    // Calcular el total de la compra
    calculateTotal(){
      return this.cart?.reduce(
        (total, item) => total + item.price * item.selectedQuantity,
        0
      );
    }
  
    // Método para manejar el checkout
  agregar_al_carrito(): void {
      if (this.checkoutActionLoading || this.cart?.length === 0) {
        return;
      }
      if (!this.formservice?.profile) {
        Swal2.fire({
          title: 'Profile required',
          position: 'top-end',
          timerProgressBar: true,
          toast: true,
          text: 'Please complete your profile before opening the checkout cart.',
          icon: 'warning',
          confirmButtonText: 'Understood',
          color: '#ffffff',
          background: '#010f4e',
        });
        return;
      }

      this.checkoutActionLoading = 'cart';
      this.cart.forEach((newItem) => {
        // Find the index of the existing item in AlibabaCheckoutCart
        const existingIndex = this.formservice.AlibabaCheckoutCart.findIndex(
          (item) => item.sku_id === newItem.sku_id && item.product_id === newItem.product_id
        );
      
        if (existingIndex !== -1) {
          // If the item exists, update its quantity
          const existingItem = this.formservice.AlibabaCheckoutCart[existingIndex];
          this.formservice.AlibabaCheckoutCart[existingIndex] = {
            ...existingItem,
            selectedQuantity: existingItem.selectedQuantity + newItem.selectedQuantity,
          };
        } else {
          // If the item doesn't exist, add it as a new item
          this.formservice.AlibabaCheckoutCart.push(newItem);
        }
      });
      


      console.log('Productos seleccionados:', this.cart);
      this.request.createRequestPost('ControladorDeCarritoDeCompras', {
        update_cart: true,
        add_items: true,
        cart_items: this.cart
      }).then((data) => {
        if (data && typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (error) {
            console.error('Error parsing data:', error);
          }
        }
        console.log('data', data);
        this.socialNotifications.notifyLocal(
          'Producto agregado',
          'El producto se agregó correctamente a tu carrito.',
          { event_type: 'cart_item_added' },
        );
        Swal2.fire({
          title: 'Cart updated',
          position: 'top-end',
          timerProgressBar: true,
          toast: true,
          text: 'Your cart has been updated successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
          color: '#ffffff',
          background: '#010f4e',
        });

        this.router.navigateByUrl('/feex-wholesale').then(() => {
          this.closeTopModals(3);
        });
      }).catch((error) => {
        console.error('Error updating checkout cart:', error);
        Swal2.fire({
          title: 'No pudimos actualizar el carrito',
          position: 'top-end',
          timer: 2200,
          timerProgressBar: false,
          toast: true,
          text: 'Intenta de nuevo en unos segundos.',
          icon: 'error',
          color: '#ffffff',
          background: '#010f4e',
        });
      }).finally(() => {
        this.checkoutActionLoading = null;
      });
      // Aquí puedes redirigir al usuario o realizar la lógica necesaria para procesar el pedido.
    }
    comprar_ahora(){
      if (this.checkoutActionLoading || this.cart?.length === 0) {
        return;
      }
      this.checkoutActionLoading = 'buy';
      console.log('Productos seleccionados:', this.cart);
      this.formservice.AlibabaCheckoutCart = this.cart;
      this.router.navigate(['/alibaba-checkout-cart'], {
        queryParams: {
          cart: JSON.stringify(this.cart),
        },
      }).finally(() => {
        this.checkoutActionLoading = null;
      });
    }
    private async closeTopModals(count: number): Promise<void> {
      for (let i = 0; i < count; i++) {
        const topModal = await this.modalController.getTop();
        if (!topModal) {
          break;
        }
        await topModal.dismiss();
      }
    }
    private requestedProductIds = new Set<string>(); // Store requested product IDs
    async handleImageError(event: Event, product_id: string) {
      console.log('Product ID:', product_id);
      // Check if the product ID has already been requested
      if (this.requestedProductIds.has(product_id)) {
        console.log(`Skipping duplicate request for product ID: ${product_id}`);
        return;
      }
    
      const imgElement = event.target as HTMLImageElement;
    
      try {
     
     
  
     
        // Extract the mobileDetail string
        let detail = this.complete_data.success.aliexpress_ds_product_get_response.result.ae_item_base_info_dto.mobile_detail;
  
        // Parse JSON safely
        let parsedDetail;
        if(!detail){
          this.requestedProductIds.add(product_id); // Mark product ID as requested
          // Do not set the bad Laniakea fallback GIF anymore — the template now hides any fallback image
          // and shows rich product content instead.
          console.warn('No mobile detail available for product image — will hide image and show product info');
          return;
        }
        try {
          parsedDetail = JSON.parse(detail);
        } catch (error) {
          console.error('Invalid JSON format:', error);
          return;
        }
    
        if (!parsedDetail?.moduleList) {
          console.error('No moduleList found in the response — hiding image and showing product content instead');
          return;
        }
    
        // Function to validate image URLs
        const validateImage = async (url: string): Promise<boolean> => {
          try {
            const response = await fetch(url, { method: 'HEAD' });
            return response.ok;
          } catch (error) {
            return false;
          }
        };
    
        // Find and set the first valid image
        for (const module of parsedDetail.moduleList) {
          if (module.type === 'image' && module.images) {
            for (const image of module.images) {
                if (image.imgUrl) {
                    const isValid = await validateImage(image.imgUrl);
                    if (isValid) {
                        imgElement.src = image.imgUrl;
                        console.log('Valid image set:', image.imgUrl);
                        this.requestedProductIds.add(product_id); // Mark product ID as requested
                        return;
                    }
                }
            }
          }
          if (module.type === 'image' && module.data?.url) {
            const isValid = await validateImage(module.data.url);
            if (isValid) {
              imgElement.src = module.data.url;
              console.log('Valid image set:', module.data.url);
              this.requestedProductIds.add(product_id); // Mark product ID as requested
              return;
            }
          }
        
        }
        console.warn('No valid image found for the product — template will hide the image and display rich product content instead');
  
        this.requestedProductIds.add(product_id); // Mark product ID as requested
   
      } catch (error) {
        console.error('Error fetching product data:', error);
      }
    }
    async dismiss(): Promise<void> {
      // This component is normally a route, not a modal. Return to the
      // clean FEEX route instead of restoring the product_id deep link.
      if (this.router.url.startsWith('/StockKeepingUnit')) {
        await this.router.navigate(['/feex-wholesale', 'null'], { replaceUrl: true });
        return;
      }

      const topModal = await this.modalController.getTop();
      if (topModal) {
        await topModal.dismiss();
        return;
      }
      this.location.back();
    }
    getStars(rating: number): { icon: string, filled: boolean }[] {
      const stars: { icon: string, filled: boolean }[] = [];
      const fullStars = Math.floor(rating);
      const decimal = rating % 1;
    
      // Add filled stars
      for (let i = 0; i < fullStars; i++) {
        stars.push({ icon: 'star', filled: true });
      }
    
      // Add half star if decimal >= 0.5 and not at 5 stars
      if (decimal >= 0.5 && stars.length < 5) {
        stars.push({ icon: 'star-half', filled: true });
      }
    
      // Fill remaining with empty stars
      while (stars.length < 5) {
        stars.push({ icon: 'star-outline', filled: false });
      }
    
      return stars;
    }
    view_stock(){
      this.product_details=false;
    }
}
